import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { apiGet, apiPost, ApiError } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'
import { FilterGroup } from '../universe/FilterGroup.tsx'
import { VoterResults } from '../universe/VoterResults.tsx'
import {
  countConditions,
  emptyGroup,
  fromAst,
  toAst,
  type DispositionMeta,
  type FieldMeta,
  type GroupNode,
  type SurveyQuestionMeta,
} from '../universe/model.ts'

/**
 * The universe builder, for a new definition or an existing one.
 *
 * The count is live because speed is the product claim; saving is explicit
 * because a definition is a thing someone will later stake a mail drop on.
 */

interface FieldsResponse {
  fields: FieldMeta[]
}
interface CountResponse {
  count: number
  took_ms: number
}
interface LoadedUniverse {
  universe_id: string
  campaign_id: string
  name: string
  description: string | null
  version_no: number
  ast: unknown
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return debounced
}

export function UniverseBuilder() {
  const params = useParams({ strict: false }) as { universeId?: string }
  const universeId = params.universeId
  const navigate = useNavigate()
  const client = useQueryClient()

  const { campaignId, campaign, select } = useSelectedCampaign()
  const [root, setRoot] = useState<GroupNode>(() => emptyGroup('and'))
  const [name, setName] = useState('')
  const [showAst, setShowAst] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [savedVersion, setSavedVersion] = useState<number | null>(null)

  // The canonical outcome vocabulary, for contact conditions. Fetched rather
  // than hardcoded for the same reason the field list is: adding a disposition
  // is a seed row, and a copy in the browser would go stale silently.
  const { data: dispositionsData } = useQuery({
    queryKey: ['dispositions'],
    queryFn: () => apiGet<{ dispositions: DispositionMeta[] }>('/dispositions'),
  })

  const { data: fieldsData } = useQuery({
    queryKey: ['universe', 'fields'],
    queryFn: () => apiGet<FieldsResponse>('/universe/fields'),
    staleTime: Infinity,
  })

  const { data: surveyQuestionsData } = useQuery({
    queryKey: ['surveys', 'filterable', campaignId],
    queryFn: () =>
      apiGet<{ questions: SurveyQuestionMeta[] }>(`/surveys/filterable?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })

  const { data: loaded } = useQuery({
    queryKey: ['universe', universeId],
    queryFn: () => apiGet<LoadedUniverse>(`/universes/${universeId}`),
    enabled: Boolean(universeId),
  })

  // Load an existing definition back into the editor once. Re-running on every
  // render would fight the user's edits.
  useEffect(() => {
    if (!loaded) return
    setRoot(fromAst(loaded.ast))
    setName(loaded.name)
    setSavedVersion(loaded.version_no)
    if (loaded.campaign_id !== campaignId) select(loaded.campaign_id)
  }, [loaded])

  const ast = useMemo(() => toAst(root), [root])
  const astKey = JSON.stringify(ast)
  const debouncedKey = useDebounced(astKey, 350)

  const { data: countData, isFetching, error } = useQuery({
    queryKey: ['universe', 'count', campaignId, debouncedKey],
    queryFn: () =>
      apiPost<CountResponse>('/universe/count', {
        campaign_id: campaignId,
        ast: JSON.parse(debouncedKey) as unknown,
      }),
    // Counting is gated on can_run_counts, not can_build_universes: a Campaign
    // Worker may explore a universe's size even though they cannot save one.
    enabled: Boolean(
      campaignId && debouncedKey && debouncedKey !== 'null' && campaign?.can_run_counts,
    ),
  })

  async function save() {
    if (!ast || !name.trim()) return
    setSaving(true)
    setSaveError(null)
    try {
      const saved = await apiPost<{ universe_id: string; version_no: number }>('/universes', {
        campaign_id: campaignId,
        ...(universeId ? { universe_id: universeId } : {}),
        name: name.trim(),
        ast,
        // Stored alongside the definition so the list can show what it counted
        // when saved, with its timestamp.
        ...(countData ? { row_count: countData.count } : {}),
      })
      setSavedVersion(saved.version_no)
      await client.invalidateQueries({ queryKey: ['universes', campaignId] })
      if (!universeId) {
        void navigate({ to: '/universes/$universeId', params: { universeId: saved.universe_id } })
      }
    } catch (caught) {
      setSaveError(
        caught instanceof ApiError ? caught.message : 'Could not save that universe.',
      )
    } finally {
      setSaving(false)
    }
  }

  const conditions = countConditions(root)
  const canSave = Boolean(ast && name.trim() && campaign?.can_build_universes && !saving)

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[16rem] flex-1">
          <label htmlFor="universe-name" className="sr-only">
            Universe name
          </label>
          <input
            id="universe-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name this universe"
            className="w-full max-w-md rounded-md border border-line bg-surface px-3 py-2 text-lg font-semibold tracking-tight text-ink outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25"
          />
          <p className="mt-1 text-sm text-ink-muted">
            {savedVersion ? `Saved as version ${savedVersion}.` : 'Not saved yet.'} Each save keeps
            the previous version, so exports stay traceable.
          </p>
        </div>
      </div>

      {campaign ? (
        <p className="mt-3 text-xs text-ink-muted">
          Counting under <span className="font-medium text-ink">{campaign.name}</span>’s
          permitted-use authorisation. Every count is recorded against it.
        </p>
      ) : null}

      {campaign && !campaign.can_build_universes ? (
        <p className="mt-3 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">
          You can run counts on this campaign, but saving a universe needs a
          Campaign Manager role.
        </p>
      ) : null}

      <div className="mt-5 rounded-lg border border-line bg-surface-raised p-3">
        {fieldsData ? (
          <FilterGroup
            node={root}
            fields={fieldsData.fields}
            dispositions={dispositionsData?.dispositions ?? []}
            surveyQuestions={surveyQuestionsData?.questions ?? []}
            onChange={setRoot}
          />
        ) : (
          <p className="py-4 text-sm text-ink-muted">Loading fields…</p>
        )}
      </div>

      <VoterResults
        campaignId={campaignId}
        ast={ast}
        enabled={Boolean(ast && campaign?.can_view_voters)}
      />

      <div className="sticky bottom-0 mt-4 border-t border-line bg-surface/95 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium tracking-wide text-ink-muted uppercase">
            Matching voters
          </span>
          <span className="font-mono text-3xl font-semibold tabular-nums text-ink">
            {ast === null || error ? '—' : countData ? countData.count.toLocaleString() : '…'}
          </span>
          {countData && !isFetching ? (
            <span className="text-xs text-ink-muted">in {countData.took_ms} ms</span>
          ) : null}
          {isFetching ? <span className="text-xs text-ink-muted">counting…</span> : null}

          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-ink-muted">
              {conditions === 0
                ? 'No conditions yet'
                : `${conditions} condition${conditions === 1 ? '' : 's'}`}
            </span>
            <button
              type="button"
              onClick={save}
              disabled={!canSave}
              className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : universeId ? 'Save new version' : 'Save universe'}
            </button>
          </div>
        </div>

        {error ? (
          <p className="mt-2 text-sm text-ink">
            {error instanceof Error ? error.message : 'Could not run that count.'}
          </p>
        ) : null}
        {saveError ? <p className="mt-2 text-sm text-ink">{saveError}</p> : null}

        <button
          type="button"
          onClick={() => setShowAst((v) => !v)}
          className="mt-2 text-xs text-ink-muted underline underline-offset-2 hover:text-ink"
        >
          {showAst ? 'Hide' : 'Show'} the saved definition
        </button>

        {showAst ? (
          <pre className="mt-2 max-h-56 overflow-auto rounded-md border border-line bg-surface-sunken p-3 font-mono text-xs text-ink">
            {ast === null ? '// add a condition' : JSON.stringify(ast, null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  )
}
